import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerBookingTable } from '../../utils/constants';
import { BookingList } from '../../components/ui/list/Bookings';
import Pagination from '../../components/atoms/Pagination';
import { useNavigate } from 'react-router-dom';
import { buildBookingDetailState } from '../../utils/bookingNavigation';
import { GETBookings } from '../../services/booking';
import { IBooking } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import BookingFilter, {
  BookingDateFilterData,
} from '../../components/ui/filters/booking';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';

const Booking = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [totalbooking, setTotalBookings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);

  const navigate = useNavigate();

  useUrlSearchQuery((query) => {
    setSearchQuery(query);
    setPage(1);
  });

  const fetchBookings = async (
    page: number,
    limit: number,
    searchQuery?: string,
    startDate?: Date | null,
    endDate?: Date | null,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETBookings({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        status: 'PAYMENT_PENDING,COMPLETE,PAID,CANCELLED',
        startDate,
        endDate,
      });
      setBookings(response.data);
      setTotalBookings(response.count);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    !filterFlag && fetchBookings(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery, sort]);

  useEffect(() => {
    filterFlag &&
      fetchBookings(page, rowsPerPage, searchQuery, startDate, endDate);
  }, [startDate, endDate, page, rowsPerPage, searchQuery, sort]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleOpenManageDrawer = (id: string | number) => {
    navigate(`/bookings/${id}`, {
      state: buildBookingDetailState('bookings'),
    });
  };

  const handleAdd = () => {
    navigate(`/bookings/create`);
  };
  const reloadDefaultList = () => {
    setFilterFlag(false);
    setStartDate(null);
    setEndDate(null);
    fetchBookings(page, rowsPerPage, searchQuery);
  };
  const handleFilterRefresh = (Filter: BookingDateFilterData) => {
    if (Filter.startDate && Filter.endDate) {
      setStartDate(new Date(Filter.startDate));
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        <SmartFilter
          title="Bookings"
          createFilter={['SEARCH', 'ADD', 'FILTER']}
          filterHeader={'Filter Services'}
          filterElement={
            <BookingFilter
              onResetFilter={reloadDefaultList}
              startDate={
                startDate
                  ? new Date(startDate).toISOString().split('T')[0]
                  : null
              }
              endDate={
                endDate ? new Date(endDate).toISOString().split('T')[0] : null
              }
            />
          }
          addHeader={'Add Services'}
          onNavigate={handleAdd}
          onSearch={handleSearchRefresh}
          addDrawerSize="md"
          onFiltered={handleFilterRefresh}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
          <Box
            sx={{
              height: '100%',
              maxHeight: '100%',
              overflowY: 'scroll',
              scrollbarWidth: 'none',
            }}
          >
            {bookings.length <= 0 && !isLoading ? (
              <Typography textAlign={'center'} marginTop={'20%'}>
                No Bookings Found
              </Typography>
            ) : (
              <BookingList
                headerCells={headerBookingTable}
                bookings={bookings}
                isLoading={isLoading}
                onManageClick={handleOpenManageDrawer}
                onlyview
              />
            )}
          </Box>
          <Box display={'block'} bottom={0} right={0}>
            <Pagination
              count={totalbooking}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default withComponentAuth(Booking);
